import { ConnectionError } from '@lucidlink/sdk';
import { webCrypto } from '@lucidlink/ts-crypto/web-crypto';
import { DOWNLOAD_ENTRY_KINDS, DOWNLOAD_START_FAILURE_STAGES, } from '../../constants';
import { createDownloadLockName } from '../create-download-lock-name';
import { createDownloadReporter } from './create-download-reporter';
import { describeFileDownload } from './describe-file-download';
import { describeFolderDownload } from './describe-folder-download';
import { initiateDownload } from './initiate-download';
import { trackRetries } from './retry-tracker.service';
import { setupDownloadServiceWorker } from './setup-download-service-worker';
import { withNetworkRetry } from './with-network-retry';
const describeEntryDownload = (args, session) => {
    switch (args.kind) {
        case DOWNLOAD_ENTRY_KINDS.FILE:
            return describeFileDownload(args, session);
        case DOWNLOAD_ENTRY_KINDS.FOLDER:
            return describeFolderDownload(args, session);
    }
};
const runEntryDownload = async (args, env, scope) => {
    const { plugConfiguredFilespaceClient } = args;
    const abortController = new AbortController();
    const { onRetry, retryFacts } = trackRetries();
    // Before any content is read, a CORS block and a network drop look the same.
    let isFileContentRead = false;
    const hasReadFileContent = () => isFileContentRead;
    const createChunkDownloader = ({ fsEntryKey, isExternalFile = false, }) => {
        // An external file is read from another bucket, so only its own first
        // chunk rules out a CORS block.
        let isOwnContentRead = false;
        const canRetry = (error) => {
            if (!hasReadFileContent()) {
                return false;
            }
            if (isExternalFile) {
                return error instanceof ConnectionError || isOwnContentRead;
            }
            return true;
        };
        return async (buffer, offset) => {
            const bytesRead = await withNetworkRetry(() => plugConfiguredFilespaceClient((fsClient) => fsClient
                .fileSystem()
                .readFile(fsEntryKey, buffer, offset)), { canRetry, onRetry, signal: abortController.signal });
            isFileContentRead = true;
            isOwnContentRead = true;
            return bytesRead;
        };
    };
    const descriptor = describeEntryDownload(args, {
        abortController,
        createChunkDownloader,
        onRetry,
        scope,
    });
    const report = createDownloadReporter({
        downloadArgs: args,
        entryFacts: descriptor.entryFacts,
        retryFacts,
        showDownloadStartFailedModal: env.showDownloadStartFailedModal,
    });
    if (descriptor.batch) {
        env.addActiveBatchDownload(descriptor.batch);
    }
    const untrackBatch = () => {
        if (descriptor.batch) {
            env.removeActiveBatchDownload(descriptor.batch);
        }
    };
    const attemptStart = async (failureStage, run) => {
        try {
            return await run();
        }
        catch (error) {
            report.startFail(error, failureStage);
            return null;
        }
    };
    const source = await attemptStart(DOWNLOAD_START_FAILURE_STAGES.FILESPACE_LOGIN, descriptor.openSource);
    // A failed start was already reported.
    if (!source) {
        untrackBatch();
        return;
    }
    const cleanUp = async () => {
        await source.cleanUp();
        untrackBatch();
    };
    const sw = await attemptStart(DOWNLOAD_START_FAILURE_STAGES.SERVICE_WORKER_REGISTRATION, () => setupDownloadServiceWorker({ abortController, scope }));
    if (!sw) {
        await cleanUp();
        return;
    }
    await initiateDownload({
        abortSignal: abortController.signal,
        cleanUp,
        descriptor,
        env,
        hasReadFileContent,
        report,
        source,
        sw,
    });
};
export const entryDownload = (args, env) => {
    const scope = webCrypto.randomUUID();
    return navigator.locks.request(createDownloadLockName(scope), () => runEntryDownload(args, env, scope));
};
